import React, { useState, useCallback, useEffect, useMemo } from 'react'
import HeaderTitle from '_/components/header-title'
import { Grid, InputAdornment } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { Datagrid, Button } from '@refera/ui-web'
import { navigate } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'

import { getInspectionTradesmen } from '_/modules/inspectionBudget/actions'
import { getInspectionTradesmenSelector } from '_/modules/inspectionBudget/selector'
import { ROUTES } from '_/utils/constants'
import { useToast } from '_/hooks/use-toast'
import useHandleInput from '_hooks/use-handle-input'
import SearchBar from '_components/search-bar'

import { PROVIDER_COLUMNS } from './utils/constants'
import useStyles, { datagridStyle } from './styles'

export default function InspectionBudgetProviderList() {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { showToast } = useToast()

  const [loading, setLoading] = useState(false)
  const [input, handleInput] = useHandleInput()
  const [searchValue, setSearchValue] = useState('')
  const [page, setPage] = useState(0)

  const handleSearch = useCallback(() => {
    setSearchValue(input)
    setPage(0)
  }, [input])

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter') {
        handleSearch()
      }
    },
    [handleSearch]
  )

  const inputProps = useMemo(
    () => ({
      endAdornment: (
        <InputAdornment position="end">
          <SearchIcon onClick={handleSearch} style={{ cursor: 'pointer' }} />
        </InputAdornment>
      ),
    }),
    [handleSearch]
  )

  const [pageSize, setPageSize] = useState(10)
  const [orderBy, setOrderBy] = useState('name')

  const tradesmen = useSelector(getInspectionTradesmenSelector)

  const handleOrderBy = orderObj => {
    const order = orderObj[0]
    if (!order) {
      setOrderBy('name')
      return
    }
    const { field, sort } = order
    setOrderBy(`${sort === 'desc' ? '-' : ''}${field}`)
  }

  const handleGoToProvider = useCallback(event => {
    const { id } = event.row
    navigate(`${ROUTES.INSPECTION_BUDGET_PROVIDERS}/${id}`)
  }, [])

  useEffect(() => {
    setLoading(true)

    dispatch(getInspectionTradesmen({ page: page + 1, pageSize, orderBy, name: searchValue }))
      .catch(() => {
        showToast({ type: 'error', message: 'Ocorreu um erro ao buscar prestadores' })
      })
      .finally(() => setLoading(false))
  }, [dispatch, page, pageSize, orderBy, showToast, searchValue])

  return (
    <Grid className={styles.container}>
      <HeaderTitle
        title="Prestadores - Emissor Orçamento Desocupação"
        backButtonAction={() => navigate(ROUTES.INSPECTION_BUDGET_LIST)}
      >
        <Grid className={styles.headerButtons}>
          <SearchBar
            className={styles.searchBar}
            value={input}
            onChange={handleInput}
            placeholder="Pesquisar prestador"
            InputProps={inputProps}
            onKeyDown={handleKeyDown}
          />
          <Button
            variant="primary"
            disabled={loading}
            onClick={() => navigate(`${ROUTES.INSPECTION_BUDGET_LIST}/novo-prestador`)}
          >
            Novo
          </Button>
        </Grid>
      </HeaderTitle>

      <Grid className={styles.tableContainer}>
        <Grid className={styles.mainTable}>
          <Datagrid
            rows={tradesmen?.results ?? []}
            rowCount={tradesmen?.count ?? 0}
            loading={loading}
            getRowId={row => row?.id}
            columns={PROVIDER_COLUMNS}
            paginationMode="server"
            onPageChange={setPage}
            page={page}
            onPageSizeChange={setPageSize}
            pageSize={pageSize}
            onSortModelChange={handleOrderBy}
            sx={datagridStyle}
            onRowClick={handleGoToProvider}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}
